import { $ } from '../../../shared/js/utils/dom';
import { createFocusTrap } from 'focus-trap';
import Bus from '../../../shared/js/modules/event-bus';

class InputHint {
  constructor(params) {
    this.el = params.el;
    this.toggleEl = params.toggleEl;
    this.dialogEl = params.dialogEl;
    this.dialogArrowEl = params.dialogArrowEl;
    this.closeEl = params.closeEl;
    this.uniqueStr = `${Math.floor(
      Math.random() * Math.floor(Math.random() * Date.now())
    )}`;
    this.id = `hint-${this.uniqueStr}`;
    this.isOpen = false;
    this.focusTrapInstance = undefined;
  }

  init() {
    this.eventBinding();
    this.addAria();
    this.updatePosition();
  }

  eventBinding() {
    this.toggleEl.addEventListener('click', this.toggle.bind(this));
    this.closeEl.addEventListener('click', this.close.bind(this));
    this.el.addEventListener('keyup', this.keyupEscHandler.bind(this));
    Bus.subscribe(
      'resize',
      'HintPositionResize',
      this.updatePosition.bind(this)
    );
  }

  eventUnbinding() {
    this.toggleEl.removeEventListener('click', this.toggle);
    this.closeEl.removeEventListener('click', this.close);
    this.el.removeEventListener('keyup', this.keyupEscHandler);
    Bus.unsubscribe('resize', 'HintPositionResize', this.updatePosition);
  }

  toggle(e) {
    e?.preventDefault();

    if (this.isOpen) {
      this.close();
      return;
    }

    this.open();
  }

  open(e) {
    e?.preventDefault();

    this.isOpen = true;
    this.toggleEl.setAttribute('aria-expanded', true);
    this.dialogEl.setAttribute('aria-hidden', false);
    this.addFocusTrap();
    this.updatePosition();
  }

  close(e) {
    e?.preventDefault();

    this.removeFocusTrap();
    this.toggleEl.setAttribute('aria-expanded', false);
    this.dialogEl.setAttribute('aria-hidden', true);
    this.isOpen = false;
  }

  updatePosition() {
    this.dialogEl.style.marginLeft = '';
    this.dialogEl.style.maxWidth = '';
    this.dialogEl.style.width = '';
    this.dialogArrowEl.style.marginLeft = '';
    this.dialogEl.classList.remove('is-above');
    this.dialogEl.classList.remove('is-below');
    const horizontalBuffer = 10;
    const currentPos = this.dialogEl.getBoundingClientRect();
    const leftOverlow = currentPos.x - horizontalBuffer;
    const rightOverflow =
      window.innerWidth - horizontalBuffer - (currentPos.x + currentPos.width);

    // If there is left viewport overflow
    if (leftOverlow < 0) {
      this.dialogEl.style.marginLeft = `${
        -1 * currentPos.x + horizontalBuffer
      }px`;
      this.dialogArrowEl.style.marginLeft = `${
        currentPos.x - horizontalBuffer
      }px`;
    }

    // If there is right viewport overflow
    if (rightOverflow < 0) {
      this.dialogEl.style.marginLeft = `${rightOverflow}px`;
      this.dialogArrowEl.style.marginLeft = `${-1 * rightOverflow}px`;
    }

    // If viewport gets too narrow
    this.dialogEl.style.width = `${window.innerWidth - 2 * horizontalBuffer}px`;
    this.dialogArrowEl.style.width = `${
      -1 * (window.innerWidth - 2 * horizontalBuffer)
    }px`;

    // Place above or below hint icon
    const verticalOrientation =
      currentPos.top > horizontalBuffer ? 'above' : 'below';
    this.dialogEl.classList.add(`is-${verticalOrientation}`);
  }

  addAria() {
    this.dialogEl.setAttribute('id', this.id);
    this.dialogEl.setAttribute('role', 'dialog');
    this.dialogEl.setAttribute('aria-hidden', true);
    this.toggleEl.setAttribute('aria-controls', this.id);
    this.closeEl.setAttribute('aria-controls', this.id);
  }

  removeAria() {
    this.dialogEl.removeAttribute('id');
    this.dialogEl.removeAttribute('role');
    this.dialogEl.removeAttribute('aria-hidden');
    this.toggleEl.removeAttribute('aria-controls');
    this.closeEl.removeAttribute('aria-controls');
  }

  addFocusTrap() {
    this.focusTrapInstance = createFocusTrap(this.dialogEl, {
      clickOutsideDeactivates: true,
      initialFocus: this.closeEl,
    });
    this.focusTrapInstance.activate();
  }

  removeFocusTrap() {
    this.focusTrapInstance?.deactivate();
  }

  keyupEscHandler(e) {
    if (e.key === 'Escape') this.close();
  }

  destroy() {
    this.eventUnbinding();
    this.removeAria();
    this.removeFocusTrap();
  }
}

const elements = document.querySelectorAll('.hint');
const instances = [];

elements.forEach(el => {
  const toggleEl = $('.hint__toggle', el);
  const dialogEl = $('.hint__dialog', el);
  const dialogArrowEl = $('.hint__dialog-arrow', dialogEl);
  const closeEl = $('.hint__dialog-close', dialogEl);
  const hint = new InputHint({
    el,
    toggleEl,
    dialogEl,
    dialogArrowEl,
    closeEl,
  });
  hint.init();
  instances.push(hint);
});
